import React, { useEffect, useRef, useState } from "react";
import { StaticTreeDataProvider } from "react-complex-tree";

import styled from "styled-components";

import Icon from "components/atoms/Icon";
import Tooltip from "components/atoms/Tooltip";
import Alert from "components/atoms/alert/Alert";
import Confirm from "components/atoms/alert/Confirm";
import Button from "components/atoms/button";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import ContextMenu from "components/molecules/ContextMenu";
import {
    TreeItemArrow,
    TreeItemChildren,
    TreeItemContainer,
    TreeItemKnob,
    TreeItemMain,
    TreeItemTitle,
} from "components/molecules/tree/Tree";
import TreeEnvironmentWrapper from "components/molecules/tree/TreeEnvironmentWrapper";
import AbstractModal from "components/page/modal/AbstractModal";
import FolderEditModal from "components/page/modal/FolderEditModal";

import {
    useFolderChangeOrder,
    useFolderCreate,
    useFolderDelete,
    useFolderMerge,
    useFolderReferenceCreate,
    useFolderUpdate,
    useFolders,
} from "hooks/queries/useBibliographies";

const Container = styled.div`
    width: 100%;
`;

const FolderContainer = styled.div`
    min-height: 300px;
`;

const NormalView = styled.div`
    display: flex;
    padding: 8px;
    width: 100%;
    min-height: 240px;
    max-height: 320px;
    background-color: var(--color-Base1);
    border-radius: 4px;
    overflow: hidden;
`;

const RefTagWrap = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
`;

const BlankBox = styled.div`
    width: 20px;
    height: 20px;
`;

const IconBox = styled.div`
    display: flex;
    align-items: center;
    margin: 0 8px 0 0;
    width: 10px;
    height: 24px;
`;

const IconWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    border-radius: 2px;

    &:hover {
        background-color: var(--color-ButtonHover4);
    }
`;

const RefTagBody = styled.div`
    min-height: 0;
    padding-right: 4px;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
`;

const FolderAddButtonWrap = styled.div`
    display: flex;
    justify-content: flex-end;
    margin: 16px 0 0 0;
`;

function ReferenceFolderEditModal(props) {
    const { exitModal, referenceIds, selectedFolderId } = props;
    const referenceFolderCreate = useFolderReferenceCreate();
    const [showFolderAddModal, setShowFolderAddModal] = useState({
        type: "",
    });
    const [selectedItem, setSelectedItem] = useState(selectedFolderId ? [selectedFolderId] : []);
    const [folders, setFolders] = useState({
        root: {
            index: "root",
            name: "root",
        },
    });

    const folderQuery = useFolders();
    const folderUpdate = useFolderUpdate();
    const folderCreate = useFolderCreate();
    const folderDelete = useFolderDelete();
    const folderChangeOrder = useFolderChangeOrder();
    const folderMerge = useFolderMerge();
    const { setToast } = useToast();

    const tree = useRef(null);
    const treeId = "reference-folder-view";
    const [viewState, setViewState] = useState({
        [treeId]: {
            selectedItems: [],
            expandedItems: sessionStorage.getItem(`${treeId}-expand-items`)
                ? JSON.parse(sessionStorage.getItem(`${treeId}-expand-items`))
                : [],
        },
    });

    useEffect(() => {
        setViewState((prevState) => ({
            ...prevState,
            [treeId]: {
                ...prevState[treeId],
                selectedItems: selectedItem,
            },
        }));
    }, [selectedItem]);

    // 각 부모의 자식 노드를 재귀적으로 정렬하는 함수
    const buildTreeWithOrdering = (items, parentId) => {
        const children = Object.values(items)
            .filter((item) => item.parent === parentId)
            .sort((a, b) => a.order - b.order);

        // 각 자식 노드에 대해 재귀적으로 정렬

        children.forEach((child) => {
            child.children = buildTreeWithOrdering(items, child.index).map((childItem) => childItem.index);
        });
        return children;
    };

    const onCollapseItem = (item) => {
        const expandedItems = viewState[treeId].expandedItems.filter((id) => id !== item.index);
        setViewState((prevState) => ({
            ...prevState,
            [treeId]: {
                ...prevState[treeId],
                expandedItems: expandedItems,
            },
        }));
        sessionStorage.setItem(`${treeId}-expand-items`, JSON.stringify(expandedItems));
    };

    const onExpandItem = (item) => {
        const expandedItems = [...viewState[treeId].expandedItems, item.index];
        setViewState((prevState) => ({
            ...prevState,
            [treeId]: {
                ...prevState[treeId],
                expandedItems: expandedItems,
            },
        }));
        sessionStorage.setItem(`${treeId}-expand-items`, JSON.stringify(expandedItems));
    };

    useEffect(() => {
        if (folderQuery.data) {
            const items = {};
            // 아이템을 `index`를 키로 하여 맵에 저장
            folderQuery.data.forEach((folder) => {
                items[folder.id] = {
                    index: folder.id,
                    name: folder.name,
                    children: folder.children,
                    order: folder.order,
                    parent: folder.parent,
                    isFolder: folder.children.length > 0,
                    reference_count: folder.reference_count,
                };
            });

            // 재귀적으로 트리를 정렬
            const rootChildren = buildTreeWithOrdering(items, null).map((item) => item.index);

            // `root` 노드에 최상위 레벨의 자식 노드를 추가
            items["root"] = {
                index: "root",
                name: "root",
                children: rootChildren,
            };

            setFolders(items);
        }
    }, [folderQuery.data]);

    const dataProvider = new StaticTreeDataProvider(folders, (item, name) => ({
        ...item,
        name,
    }));

    useEffect(() => {
        const allNodeIds = Object.keys(folders);
        dataProvider.onDidChangeTreeDataEmitter.emit(allNodeIds);
    }, [folders]);

    dataProvider.onChangeItemChildren = function (itemId, newChildren) {
        const item = this.data.items[itemId];
        item.children = newChildren;
        item.isFolder = newChildren.length > 0;
        this.onDidChangeTreeDataEmitter.emit([itemId]);
    };

    const removeItem = (itemIds = undefined) => {
        if (!itemIds) {
            itemIds = props.viewState[treeId].selectedItems;
        }

        const deleteItems = new Set(); // 중복을 제거하기 위해 Set 사용

        const addToDeleteItems = (itemId) => {
            if (!deleteItems.has(itemId)) {
                deleteItems.add(itemId);
                const children = folders[itemId].children || [];
                children.forEach(addToDeleteItems);
            }
        };

        itemIds.forEach(addToDeleteItems);

        Confirm("warn", "폴더 삭제", "폴더를 삭제하시겠습니까? 하위 폴더들도 함께 삭제됩니다.", "삭제", () => {
            folderDelete.mutate(
                {
                    ids: Array.from(deleteItems),
                },
                {
                    onSuccess: (data, variables, context) => {
                        setToast("폴더가 삭제되었습니다.", "info");
                        const selectedItem = viewState[treeId].selectedItems[0];
                        if (deleteItems.has(selectedItem)) {
                            setSelectedItem([]);
                        }
                    },
                    onError: (error) => {
                        setToast("폴더 삭제에 실패했습니다. 잠시후 시도해주세요.", "error");
                    },
                },
            );
        });
    };

    const [showContextMenu, setShowContextMenu] = useState({
        index: null,
        x: 0,
        y: 0,
    });
    const [showTooltip, setShowTooltip] = useState(false);

    return (
        <AbstractModal
            modalTitle="폴더 선택"
            width={480}
            exitModal={exitModal}
            footer={{
                leftBtnText: "취소",
                rightBtnText: "적용",
                leftOnClick: exitModal,
                rightOnClick: () => {
                    referenceFolderCreate.mutate(
                        {
                            folder_id: selectedItem[0],
                            reference_ids: referenceIds,
                        },
                        {
                            onSuccess: () => {
                                exitModal();
                                setToast("선택한 폴더에 참고문헌이 추가되었습니다.", "info");
                            },
                            onError: () => {
                                Alert("warn", "폴더 연결 실패", "폴더 연결에 실패했습니다.");
                            },
                        },
                    );
                },
            }}
        >
            <Container>
                <FolderContainer>
                    <FolderAddButtonWrap>
                        <Button
                            width={"100px"}
                            height={"24px"}
                            bgColor={"var(--color-Button1)"}
                            hoverBgColor={"var(--color-ButtonHover1)"}
                            fontColor={"var(--color-White)"}
                            buttonText={"새 폴더"}
                            onClick={() =>
                                setShowFolderAddModal({
                                    type: "create",
                                    parentIndex: selectedItem[0] ? selectedItem[0] : null,
                                })
                            }
                            margin={"0 0 10px 0"}
                        >
                            <Icon name={"plus"} size={"12"} color={"var(--color-White)"} />
                        </Button>
                    </FolderAddButtonWrap>

                    <NormalView>
                        <RefTagWrap>
                            <RefTagBody>
                                <TreeItemContainer
                                    depth={0}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedItem([]);
                                        tree?.current.unselectAllItems();
                                    }}
                                >
                                    <TreeItemMain isSelected={selectedItem?.length === 0 && true}>
                                        <TreeItemTitle>
                                            <IconBox>
                                                {selectedItem?.length === 0 ? (
                                                    <Icon name={"check"} size={"10px"} color={"var(--color-DarkKey)"} />
                                                ) : (
                                                    <Icon name={"folder"} size={"10px"} color={"#9FA8AB"} />
                                                )}
                                            </IconBox>
                                            <Text
                                                variant={selectedItem?.length === 0 ? "body-md-500" : "body-md-400"}
                                                color={
                                                    selectedItem?.length === 0
                                                        ? "var(--color-Black)"
                                                        : "var(--color-SubBlack)"
                                                }
                                            >
                                                모든 참고문헌
                                            </Text>
                                        </TreeItemTitle>
                                    </TreeItemMain>
                                </TreeItemContainer>
                                <TreeEnvironmentWrapper
                                    treeId={treeId}
                                    ref={tree}
                                    dataProvider={dataProvider}
                                    viewState={viewState}
                                    multiSelect={false}
                                    canDropOnFolder={true}
                                    canDropOnNonFolder={true}
                                    onSelectItems={(selectedItems) => {
                                        setSelectedItem(selectedItems);
                                    }}
                                    onDrop={async (updatedNodes) => {
                                        try {
                                            await folderChangeOrder.mutateAsync(updatedNodes);
                                            setToast("폴더 순서가 변경되었습니다.", "info");
                                        } catch (error) {
                                            if (error?.response?.status === 400) {
                                                if (error?.response?.data?.errors) {
                                                    Confirm(
                                                        "warn",
                                                        "알림",
                                                        "이미 존재하는 폴더명입니다. 병합하시겠습니까?",
                                                        "병합",
                                                        () => {
                                                            folderMerge.mutate(error.response.data.instance, {
                                                                onSuccess: () => {
                                                                    setToast("폴더가 병합되었습니다.", "info");
                                                                },
                                                                onError: () => {
                                                                    Alert("error", "폴더 병합에 실패했습니다");
                                                                },
                                                            });
                                                        },
                                                        () => {
                                                            const items = {};
                                                            folderQuery.data.forEach((folder) => {
                                                                items[folder.id] = {
                                                                    index: folder.id,
                                                                    name: folder.name,
                                                                    children: folder.children,
                                                                    order: folder.order,
                                                                    parent: folder.parent,
                                                                    isFolder: folder.children.length > 0,
                                                                    reference_count: folder.reference_count,
                                                                };
                                                            });

                                                            const rootChildren = buildTreeWithOrdering(items, null).map(
                                                                (item) => item.index,
                                                            );

                                                            items["root"] = {
                                                                index: "root",
                                                                name: "root",
                                                                children: rootChildren,
                                                            };
                                                            setFolders(items);
                                                        },
                                                    );
                                                }
                                            }
                                        }
                                    }}
                                    onCollapseItem={onCollapseItem}
                                    onExpandItem={onExpandItem}
                                    renderItem={({ item, title, depth, context, children }) => (
                                        <TreeItemContainer
                                            {...context.itemContainerWithChildrenProps}
                                            depth={Number(depth)}
                                            onMouseLeave={() =>
                                                setShowContextMenu({
                                                    index: null,
                                                    x: 0,
                                                    y: 0,
                                                })
                                            }
                                        >
                                            <TreeItemMain
                                                {...context.itemContainerWithoutChildrenProps}
                                                {...context.interactiveElementProps}
                                                isDraggingOver={context.isDraggingOver}
                                                isSelected={context.isSelected}
                                            >
                                                {item.isFolder ? (
                                                    <TreeItemArrow
                                                        {...context.arrowProps}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            if (context.isExpanded) {
                                                                tree.current?.collapseItem(item.index);
                                                            } else {
                                                                tree.current?.expandItem(item.index);
                                                            }
                                                        }}
                                                    >
                                                        <IconWrap>
                                                            <Icon
                                                                name={
                                                                    context.isExpanded
                                                                        ? "folderDownward"
                                                                        : "folderRightward"
                                                                }
                                                                size={"10px"}
                                                            />
                                                        </IconWrap>
                                                    </TreeItemArrow>
                                                ) : (
                                                    <BlankBox />
                                                )}

                                                <TreeItemTitle>
                                                    <IconBox>
                                                        {context.isSelected ? (
                                                            <Icon
                                                                name={"check"}
                                                                size={"10px"}
                                                                color={"var(--color-DarkKey)"}
                                                            />
                                                        ) : (
                                                            <Icon name={"folder"} size={"10px"} color={"#9FA8AB"} />
                                                        )}
                                                    </IconBox>
                                                    <Text
                                                        variant={context.isSelected ? "body-md-500" : "body-md-400"}
                                                        color={
                                                            context.isSelected
                                                                ? "var(--color-Black)"
                                                                : "var(--color-SubBlack)"
                                                        }
                                                        whiteSpace={"pre-wrap"}
                                                    >
                                                        {title}
                                                    </Text>
                                                    <Text
                                                        margin={"0 0 0 2px"}
                                                        variant="body-sm-400"
                                                        fontWeight={"500"}
                                                        color={"var(--color-Grey1)"}
                                                    >
                                                        &nbsp;{item.reference_count}
                                                    </Text>
                                                </TreeItemTitle>
                                                <TreeItemKnob
                                                    className={`tree-knob-${item.index}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setShowContextMenu(
                                                            item.index === showContextMenu.index
                                                                ? { index: null, x: 0, y: 0 }
                                                                : {
                                                                      index: item.index,
                                                                      x: e.currentTarget.getBoundingClientRect().left,
                                                                      y: e.currentTarget.getBoundingClientRect().bottom,
                                                                  },
                                                        );
                                                    }}
                                                >
                                                    {showTooltip ? (
                                                        <Tooltip message={"더보기"}>
                                                            <IconWrap
                                                                onMouseEnter={() => setShowTooltip(true)}
                                                                onMouseLeave={() => setShowTooltip(false)}
                                                            >
                                                                <Icon name={"submenu"} />
                                                            </IconWrap>
                                                        </Tooltip>
                                                    ) : (
                                                        <IconWrap
                                                            onMouseEnter={() => setShowTooltip(true)}
                                                            onMouseLeave={() => setShowTooltip(false)}
                                                        >
                                                            <Icon name={"submenu"} />
                                                        </IconWrap>
                                                    )}
                                                    {showContextMenu.index === item.index && (
                                                        <ContextMenu
                                                            show={true}
                                                            position={{
                                                                x: showContextMenu.x,
                                                                y: showContextMenu.y - 20,
                                                            }}
                                                            minWidth={"70px"}
                                                            items={[
                                                                {
                                                                    name: "새 폴더",
                                                                    action: () => {
                                                                        setShowFolderAddModal({
                                                                            type: "create",
                                                                            parentIndex: item.index,
                                                                        });
                                                                    },
                                                                },
                                                                {
                                                                    name: "이름 변경",
                                                                    action: () => {
                                                                        setShowFolderAddModal({
                                                                            type: "update",
                                                                            item: item,
                                                                        });
                                                                    },
                                                                },
                                                                {
                                                                    name: "삭제",
                                                                    action: () => {
                                                                        removeItem([item.index]);
                                                                    },
                                                                },
                                                            ]}
                                                            onClose={() =>
                                                                setShowContextMenu({
                                                                    index: null,
                                                                    x: 0,
                                                                    y: 0,
                                                                })
                                                            }
                                                            triggerClassName={`tree-knob-${item.index}`}
                                                        />
                                                    )}
                                                </TreeItemKnob>
                                            </TreeItemMain>
                                            <TreeItemChildren>{children}</TreeItemChildren>
                                        </TreeItemContainer>
                                    )}
                                />
                            </RefTagBody>
                        </RefTagWrap>
                    </NormalView>
                </FolderContainer>

                {showFolderAddModal.type && (
                    <FolderEditModal
                        exitModal={() =>
                            setShowFolderAddModal({
                                type: "",
                            })
                        }
                        onSelectItems={(selectedItems) => {
                            setSelectedItem(selectedItems);
                        }}
                        expandItem={(item) => {
                            tree.current?.expandItem(item);
                        }}
                        mutation={showFolderAddModal.type === "create" ? folderCreate : folderUpdate}
                        context={showFolderAddModal}
                    />
                )}
            </Container>
        </AbstractModal>
    );
}

export default ReferenceFolderEditModal;
